/**
 * @prettier
 * @flow
 */
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { Variant } from 'src/components/form/FormTypes';
import { getError } from 'src/components/form/utils/getError';
import { transformStringInput } from 'src/components/form/utils/transformStringInput';
import { transformStringOutput } from 'src/components/form/utils/transformStringOutput';
import { sleep } from 'src/utils/sleep';
import { translate } from 'src/utils/translate/translate';

export function FormSelect({ name, label, placeholder, helperText, defaultValue = null, options, disabled, required, variant = 'outlined', onChange: onChangeProp }: Props) {
    const {
        errors,
        control,
        formState: { isSubmitting },
    } = useFormContext();
    const inputRef = useRef();
    const [isSubmittingWorkaround, setIsSubmittingWorkaround] = useState(false);
    useEffect(() => {
        // Workaround since only inputRef.current.disabled = false; does not work
        async function delay() {
            await sleep(0);
            setIsSubmittingWorkaround(isSubmitting);
        }

        delay();
    }, [isSubmitting]);

    return (
        <FormControl fullWidth disabled={isSubmittingWorkaround || disabled} variant={variant}>
            <InputLabel error={!!getError(errors, name)} required={required} shrink>
                {label}
            </InputLabel>
            <Controller
                render={({ value, onChange, ...props }) => (
                    <Select
                        {...props}
                        value={transformStringInput(value)}
                        onChange={e => {
                            const value = transformStringOutput(e.target.value);
                            onChangeProp && onChangeProp(value);
                            onChange(value);
                        }}
                        placeholder={placeholder}
                        variant={variant}
                        error={!!getError(errors, name)}
                        inputProps={{
                            inputRef: inputRef,
                        }}
                    >
                        {options?.map(option => (
                            <MenuItem key={`${option.value}-${option.label}`} value={option.value}>
                                {option.label}&nbsp;
                            </MenuItem>
                        ))}
                    </Select>
                )}
                control={control}
                name={name}
                defaultValue={defaultValue}
                rules={{
                    required: {
                        value: (required: any)?.value ?? required,
                        message: (required: any)?.message ?? translate('This field is required'),
                    },
                }}
                onFocus={() => {
                    // make focus on error work when disabled={isSubmitting || disabled}
                    if (inputRef.current) {
                        inputRef.current.disabled = false;
                        inputRef.current.focus();
                    }
                }}
            />
            {(helperText ?? getError(errors, name)) && <FormHelperText error={!!getError(errors, name)}>{errors?.[name]?.message ?? helperText}</FormHelperText>}
        </FormControl>
    );
}

export type Props = {|
    name: string,
    label?: string,
    placeholder?: string,
    helperText?: string,
    defaultValue?: string,
    options: Array<{ label: string, value: any }>,
    disabled?: boolean,
    required?: boolean | { value: number, message: string },
    variant?: Variant,
    onChange?: any => any,
|};
